import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import EmailComposer from "../components/EmailComposer";
import WaDraftModal from "../components/WaDraftModal";
import { openComposerWindow, onMailChanged } from "../composerWindow";
import { Modal } from "../components/ui";

const VIEWS = [
  ["", "📥 Tutte"], ["non_lette", "● Non lette"], ["allegati", "📎 Con allegati"],
  ["importanti", "⭐ Importanti"], ["da_gestire", "🕐 Da gestire"], ["gestite", "✓ Gestite"],
];
const FOLDERS = [
  ["inbox", "📥 In arrivo"], ["sent", "📤 Inviate"], ["drafts", "📝 Bozze"],
  ["archive", "🗄 Archivio Gmail"],
];
const PAGE = 50;

const fmtTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
};
const joinTo = (t) => (Array.isArray(t) ? t.join(", ") : t || "");
const escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const hl = (text, term) => {
  const t = (term || "").trim();
  if (!t || !text) return text;
  const parts = String(text).split(new RegExp(`(${escRx(t)})`, "gi"));
  if (parts.length === 1) return text;
  return parts.map((p, i) => (i % 2 === 1
    ? <mark key={i} style={{ background: "#fde047", color: "#111", padding: 0, borderRadius: 2 }}>{p}</mark>
    : p));
};
const hlHtml = (html, term) => {
  const t = (term || "").trim();
  if (!t || !html) return html;
  const rx = new RegExp(escRx(t), "gi");
  let skip = false;
  return html.split(/(<[^>]*>)/g).map((seg) => {
    if (seg.startsWith("<")) {
      const low = seg.toLowerCase();
      if (low.startsWith("<style") || low.startsWith("<script") || low.startsWith("<head")) skip = true;
      else if (low.startsWith("</style") || low.startsWith("</script") || low.startsWith("</head")) skip = false;
      return seg;
    }
    return skip ? seg : seg.replace(rx, (m) => `<mark style="background:#fde047;color:#111">${m}</mark>`);
  }).join("");
};

const fixFrameLinks = (e) => {
  try {
    const doc = e.target.contentDocument || e.target.contentWindow.document;
    doc.querySelectorAll("a[href]").forEach((a) => { a.target = "_blank"; a.rel = "noopener noreferrer"; });
  } catch (_) {}
};


export default function Webmail() {
  const navigate = useNavigate();
  const [folder, setFolder] = useState("inbox");
  const [senders, setSenders] = useState([]);
  const [account, setAccount] = useState("");
  const [view, setView] = useState("da_gestire");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [sel, setSel] = useState(null);        // messaggio in arrivo selezionato
  const [selSent, setSelSent] = useState(null); // email inviata selezionata
  const [resumeDraft, setResumeDraft] = useState(null);
  const [waDraft, setWaDraft] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [nextToken, setNextToken] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selCompany, setSelCompany] = useState("");
  const [selProject, setSelProject] = useState("");
  const [selFolder, setSelFolder] = useState("");
  const qTimer = useRef(null);

  const load = useCallback(async (append = false, skip = 0, search = q) => {
    setLoading(true);
    try {
      if (folder === "inbox") {
        const p = new URLSearchParams({ skip, limit: PAGE });
        if (account) p.set("account", account);
        if (view) p.set("view", view);
        if (search.trim()) p.set("q", search.trim());
        const r = await api.get(`/emails/webmail?${p.toString()}`, { noCache: true });
        setRows((prev) => (append ? [...prev, ...r.data.rows] : r.data.rows));
        setTotal(r.data.total);
        setUnread(r.data.unread);
      } else if (folder === "archive") {
        if (!append) setRows([]);
        const p = new URLSearchParams();
        if (account) p.set("account", account);
        if (search.trim()) p.set("q", search.trim());
        if (append && nextToken) p.set("page_token", nextToken);
        const r = await api.get(`/emails/gmail-archive?${p.toString()}`, { noCache: true });
        setRows((prev) => (append ? [...prev, ...r.data.rows] : r.data.rows));
        setNextToken(r.data.next_page_token || null);
        setTotal(0);
      } else if (folder === "sent") {
        const p = new URLSearchParams({ limit: "500" });
        if (search.trim()) p.set("q", search.trim());
        const r = await api.get(`/emails/sent?${p.toString()}`, { noCache: true });
        const list = (r.data || []).filter((s) => s.channel !== "whatsapp" && s.channel !== "sms");
        setRows(list);
        setTotal(list.length);
      } else {
        const r = await api.get("/emails/drafts", { noCache: true });
        let list = r.data || [];
        if (search.trim()) {
          const t = search.trim().toLowerCase();
          list = list.filter((d) => (d.to || "").toLowerCase().includes(t) || (d.subject || "").toLowerCase().includes(t) || (d.body_text || "").toLowerCase().includes(t));
        }
        setRows(list);
        setTotal(list.length);
      }
    } catch { /* offline */ }
    setLoading(false);
  }, [account, view, q, folder]);

  useEffect(() => {
    api.get("/email-templates/senders").then((r) => setSenders(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setSel(null); setSelSent(null); setSelected(new Set()); load(); }, [account, view, folder]); // eslint-disable-line
  // la finestra separata ha salvato o inviato: ricarico l'elenco
  useEffect(() => onMailChanged(() => load()), [load]);
  useEffect(() => {
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => load(), 400);
    return () => clearTimeout(qTimer.current);
  }, [q]); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => { if (folder === "inbox") load(); }, 60000); return () => clearInterval(t); }, [load, folder]);

  const openRow = async (row) => {
    if (folder === "drafts") { row.channel === "whatsapp" ? setWaDraft(row) : setResumeDraft(row); return; }
    try {
      if (folder === "sent") {
        const r = await api.get(`/emails/sent/${row.id}`, { noCache: true });
        setSelSent(r.data); setSel(null);
        return;
      }
      if (folder === "archive") {
        if (!row.gmail_id) return;
        const p = account ? `?account=${encodeURIComponent(account)}` : "";
        const r = await api.get(`/emails/gmail-archive/${row.gmail_id}${p}`, { noCache: true });
        setSel({ ...r.data, archive: true, read: true }); setSelSent(null);
        return;
      }
      const r = await api.get(`/emails/${row.id}`, { noCache: true });
      setSel(r.data); setSelSent(null);
      setRows((rs) => rs.map((x) => (x.id === row.id ? { ...x, read: true, cc: r.data.cc || x.cc } : x)));
    } catch (e) { alert("Errore apertura: " + (e.response?.data?.detail || e.message)); }
  };
  const toggleStar = async () => {
    if (!sel) return;
    const r = await api.post(`/emails/${sel.id}/star`, { starred: !sel.starred });
    const starred = r.data?.starred ?? !sel.starred;
    setSel({ ...sel, starred });
    setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, starred } : x)));
  };
  const markHandled = async () => {
    if (!sel) return;
    await api.post(`/emails/${sel.id}/handle`);
    setSel({ ...sel, status: "gestita" });
    setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, status: "gestita" } : x)));
  };
  const deleteDraft = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questa bozza?")) return;
    await api.delete(`/emails/drafts/${id}`);
    load();
  };
  const openAttachment = async (i, name) => {
    try {
      const r = await api.get(`/emails/${sel.id}/attachments/${i}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = name || `allegato-${i + 1}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { alert("Errore allegato: " + e.message); }
  };

  const toggleSel = (id, e) => {
    e.stopPropagation();
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const allVisible = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allVisible ? new Set() : new Set(rows.map((r) => r.id)));
  const deleteOne = async () => {
    if (!sel || !window.confirm("Eliminare questa email?")) return;
    await api.delete(`/emails/${sel.id}`);
    setSel(null);
    load();
  };
  const deleteSent = async () => {
    if (!selSent || !window.confirm("Eliminare questo messaggio inviato dal registro?")) return;
    try { await api.delete(`/emails/sent/${selSent.id}`); setSelSent(null); load(); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
  const openSentAttachment = async (s, i) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/emails/sent/${s.id}/attachments/${i}?inline=1`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      if (w) w.location = u; else window.open(u, "_blank");
    } catch (e) { if (w) w.close(); alert("Allegato non disponibile: " + (e.response?.data?.detail || e.message)); }
  };
  const downloadSentAttachment = async (s, i, filename) => {
    try {
      const r = await api.get(`/emails/sent/${s.id}/attachments/${i}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = u; link.download = filename || "allegato";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch (e) { alert("Allegato non disponibile: " + (e.response?.data?.detail || e.message)); }
  };
  const asList = (v) => Array.isArray(v) ? v : String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
  const sentAsOriginal = (s) => {
    const tos = asList(s.to);
    return {
      from_email: tos[0] || "", from_name: "", to: tos, cc: asList(s.cc),
      subject: s.subject || "", body_html: s.body_html, body_text: s.body_text,
      received_at: s.sent_at, thread_id: s.thread_id || null,
    };
  };
  const bulkAct = async (action, label) => {
    if (action === "delete" && !window.confirm(`Eliminare ${selected.size} email selezionate?`)) return;
    try {
      const r = await api.post("/emails/bulk", { ids: [...selected], action });
      setSelected(new Set());
      if (sel && selected.has(sel.id)) setSel(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const openAssign = () => {
    setAssignOpen(true); setSelCompany(""); setSelProject(""); setSelFolder(""); setProjects([]); setFolders([]);
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  const onCompany = (cid) => { setSelCompany(cid); setSelProject(""); setSelFolder(""); setFolders([]); if (cid) api.get(`/projects?company_id=${cid}`).then((r) => setProjects(r.data)).catch(() => {}); };
  const onProject = (pid) => { setSelProject(pid); setSelFolder(""); if (pid) api.get(`/projects/${pid}`).then((r) => setFolders(r.data.folders)).catch(() => {}); };
  const confirmAssign = async () => {
    if (!selFolder) { alert("Seleziona una cartella"); return; }
    try {
      await api.post("/emails/bulk-assign", { ids: [...selected], folder_id: selFolder });
      setAssignOpen(false); setSelected(new Set()); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="webmail" data-testid="webmail-page">
      {/* colonna 1: cartelle e caselle */}
      <aside className="wm-side">
        <h3 style={{ margin: "0 0 10px" }}>Posta</h3>
        <div className="wm-group-label">Cartelle</div>
        {FOLDERS.map(([k, l]) => (
          <button key={k} className={`wm-item ${folder === k ? "active" : ""}`} onClick={() => setFolder(k)} data-testid={`wm-folder-${k}`}>
            {l}{k === "inbox" && unread > 0 ? ` (${unread})` : ""}
          </button>
        ))}
        {(folder === "inbox" || folder === "archive") && (
          <>
            <div className="wm-group-label" style={{ marginTop: 14 }}>Caselle</div>
            <button className={`wm-item ${account === "" ? "active" : ""}`} onClick={() => setAccount("")} data-testid="wm-account-all">{folder === "archive" ? "Prima casella" : "Tutte le caselle"}</button>
            {senders.map((s) => (
              <button key={s.id} className={`wm-item ${account === s.address ? "active" : ""}`} onClick={() => setAccount(s.address)} data-testid={`wm-account-${s.address}`}>{s.address}</button>
            ))}
          </>
        )}
        <div style={{ marginTop: 16 }}>
          <button className="btn sm primary" style={{ width: "100%" }} onClick={() => setModal("new")} data-testid="wm-compose">✉️ Scrivi</button>
        </div>
      </aside>

      {/* colonna 2: elenco messaggi */}
      <section className="wm-list" data-testid="wm-list">
        <div style={{ padding: "10px 12px 6px" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={folder === "archive" ? "🔍 Cerca ovunque: nome, email, oggetto, parola, allegato…" : "🔍 Cerca…"} data-testid="wm-search" style={{ flex: 1, minWidth: 0 }} />
            {folder === "inbox" && (
              <select value={view} onChange={(e) => setView(e.target.value)} data-testid="wm-view-select" style={{ maxWidth: 150 }} title="Filtri">
                {VIEWS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            )}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            {folder === "inbox" && <input type="checkbox" checked={allVisible} onChange={toggleAll} title="Seleziona tutte le visibili" data-testid="wm-select-all" style={{ margin: 0 }} />}
            <span>{folder === "inbox" ? `${total} messaggi · ${unread} non letti${selected.size ? ` · ${selected.size} selezionati` : ""}` : folder === "archive" ? `${rows.length} caricati · tutto Gmail, senza limiti di tempo` : `${total} ${folder === "sent" ? "inviate" : "bozze"}`}</span>
          </div>
          {folder === "inbox" && selected.size > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }} data-testid="wm-bulk-bar">
              <button className="btn sm danger" onClick={() => bulkAct("delete")} data-testid="wm-bulk-delete">🗑 Elimina</button>
              <button className="btn sm" onClick={() => bulkAct("read")} data-testid="wm-bulk-read">Lette</button>
              <button className="btn sm" onClick={() => bulkAct("unread")} data-testid="wm-bulk-unread">Da leggere</button>
              <button className="btn sm" onClick={() => bulkAct("handle")} data-testid="wm-bulk-handle">✓ Gestite</button>
              <button className="btn sm" onClick={() => bulkAct("reopen")} data-testid="wm-bulk-reopen">🕐 Da gestire</button>
              <button className="btn sm" onClick={openAssign} data-testid="wm-bulk-assign">🗂 Cartella</button>
              <button className="btn sm" onClick={() => setSelected(new Set())} data-testid="wm-bulk-clear">✕</button>
            </div>
          )}
        </div>
        <div className="wm-rows">
          {rows.map((r) => (
            <div key={r.id || r.gmail_id} className={`wm-row ${folder === "inbox" && !r.read ? "unread" : ""} ${(sel?.id === r.id || sel?.gmail_id === r.gmail_id || selSent?.id === r.id) ? "selected" : ""}`} onClick={() => openRow(r)} data-testid={`wm-row-${r.id || r.gmail_id}`}>
              <div className="wm-row-top">
                <span className="wm-from" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {folder === "inbox" && <input type="checkbox" checked={selected.has(r.id)} onChange={() => {}} onClick={(e) => toggleSel(r.id, e)} data-testid={`wm-check-${r.id}`} style={{ margin: 0, flexShrink: 0 }} />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{(folder === "inbox" || folder === "archive") ? hl(r.from_name || r.from_email, q) : <>A: {hl(joinTo(r.to), q) || "(nessuno)"}</>}</span>
                </span>
                <span className="wm-time">
                  {folder === "inbox" && r.starred ? "⭐ " : ""}
                  {(r.attachments_count > 0 || r.attachments?.length > 0) ? "📎 " : ""}
                  {fmtTime(r.received_at || r.sent_at || r.updated_at)}
                </span>
              </div>
              <div className="wm-subject">{r.channel === "whatsapp" ? <>💬 WhatsApp a {hl(r.wa_name || joinTo(r.to), q)}</> : hl(r.subject || "(senza oggetto)", q)}</div>
              <div className="wm-row-top">
                <span className="wm-snippet" style={{ flex: 1 }}>
                  {hl((folder === "inbox" || folder === "archive") ? `${r.cc?.length > 0 ? `Cc: ${r.cc.length} · ` : ""}${r.snippet || ""}` : (r.body_text || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 90), q)}
                </span>
                {folder === "drafts" && (
                  <>
                    <button className="btn sm" onClick={(e) => { e.stopPropagation(); openComposerWindow({ draft: r.id }); }} data-testid={`wm-draft-window-${r.id}`} title="Apri in una finestra separata">↗</button>{" "}
                    <button className="btn sm danger" onClick={(e) => deleteDraft(r.id, e)} data-testid={`wm-draft-del-${r.id}`}>🗑</button>
                  </>
                )}
              </div>
            </div>
          ))}
          {rows.length === 0 && !loading && <div className="empty" style={{ margin: 16 }}>{folder === "drafts" ? "Nessuna bozza: nel composer usa \"💾 Salva bozza\"." : "Nessun messaggio."}</div>}
          {folder === "archive" && nextToken && (
            <button className="btn sm" style={{ margin: 12 }} onClick={() => load(true)} disabled={loading} data-testid="wm-archive-more">
              {loading ? "Carico…" : "Carica più vecchie"}
            </button>
          )}
          {folder === "inbox" && rows.length < total && (
            <button className="btn sm" style={{ margin: 12 }} onClick={() => load(true, rows.length)} disabled={loading} data-testid="wm-load-more">
              {loading ? "Carico…" : `Carica altri (${total - rows.length})`}
            </button>
          )}
        </div>
      </section>

      {/* colonna 3: lettura */}
      <section className="wm-read" data-testid="wm-read">
        {folder === "drafts" ? (
          <div className="empty" style={{ margin: "auto" }}>Clicca una bozza per riprenderla nel composer.</div>
        ) : selSent ? (
          <>
            <div className="wm-toolbar" data-testid="wm-sent-toolbar">
              {(!selSent.channel || selSent.channel === "email") && (
                <>
                  <button className="btn sm primary" onClick={() => setModal("reply")} data-testid="wm-sent-reply">↩ Scrivi di nuovo</button>
                  <button className="btn sm" onClick={() => setModal("replyall")} data-testid="wm-sent-replyall">↩↩ A tutti i destinatari</button>
                  <button className="btn sm" onClick={() => setModal("forward")} data-testid="wm-sent-forward">→ Inoltra</button>
                </>
              )}
              <button className="btn sm danger" onClick={deleteSent} data-testid="wm-sent-delete">🗑 Elimina</button>
            </div>
            <div className="wm-headers" data-testid="wm-sent-headers">
              <h3 style={{ margin: "0 0 8px" }}>{hl(selSent.subject || "(senza oggetto)", q)}</h3>
              <div><strong>A:</strong> {joinTo(selSent.to)}</div>
              {selSent.cc && joinTo(selSent.cc) && <div><strong>Cc:</strong> {joinTo(selSent.cc)}</div>}
              {selSent.bcc && joinTo(selSent.bcc) && <div><strong>Ccn:</strong> {joinTo(selSent.bcc)}</div>}
              <div><strong>Data invio:</strong> {selSent.sent_at ? new Date(selSent.sent_at).toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" }) : "—"}</div>
              {selSent.kind && <div><strong>Tipo:</strong> {selSent.kind}</div>}
              {selSent.attachments?.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }} data-testid="wm-sent-attachments">
                  <strong>Allegati ({selSent.attachments.length}):</strong>
                  {selSent.attachments.map((a, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}>
                      📎 {a.filename}{a.size ? <span className="muted"> · {fmtSize(a.size)}</span> : null}
                      <button className="btn sm" onClick={() => openSentAttachment(selSent, i)} data-testid={`wm-sent-att-open-${i}`} title="Apri">👁</button>
                      <button className="btn sm" onClick={() => downloadSentAttachment(selSent, i, a.filename)} data-testid={`wm-sent-att-dl-${i}`} title="Scarica">⬇</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="wm-body">
              {selSent.body_html ? (
                <iframe title="corpo" srcDoc={hlHtml((selSent.body_html || "").replace(/src="cid:(news\d+)"/g, 'src="/api/drip/asset/$1.png"'), q)} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" onLoad={fixFrameLinks} style={{ width: "100%", height: "100%", border: 0, background: "#fff", borderRadius: 8 }} />
              ) : (
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14 }}>{hl(selSent.body_text, q)}</pre>
              )}
            </div>
          </>
        ) : !sel ? (
          <div className="empty" style={{ margin: "auto" }}>Seleziona un messaggio per leggerlo qui.</div>
        ) : (
          <>
            <div className="wm-toolbar">
              <button className="btn sm primary" onClick={() => setModal("reply")} data-testid="wm-reply">↩ Rispondi</button>
              <button className="btn sm" onClick={() => setModal("replyall")} data-testid="wm-replyall">↩↩ Rispondi a tutti</button>
              <button className="btn sm" onClick={() => setModal("forward")} data-testid="wm-forward">→ Inoltra</button>
              {!sel.archive && (
                <>
                  <button className="btn sm" onClick={toggleStar} data-testid="wm-star">{sel.starred ? "⭐ Importante" : "☆ Segna importante"}</button>
                  {sel.status !== "gestita" && <button className="btn sm" onClick={markHandled} data-testid="wm-handle">✓ Segna gestita</button>}
                  <button className="btn sm danger" onClick={deleteOne} data-testid="wm-delete">🗑 Elimina</button>
                </>
              )}
              <div className="spacer" />
              {sel.archive ? (
                <>
                  <button className="btn sm primary" onClick={async () => {
                    try {
                      const p = account ? `?account=${encodeURIComponent(account)}` : "";
                      const r = await api.post(`/emails/gmail-archive/${sel.gmail_id}/import${p}`);
                      navigate(`/email/${r.data.id}`);
                    } catch (e) { alert(e.response?.data?.detail || e.message); }
                  }} title="La importa nel CRM e apre la scheda completa: clienti, cartelle, incarichi, gestione…" data-testid="wm-archive-import">⚙️ Gestisci nel CRM</button>
                  <button className="btn sm danger" onClick={async () => {
                    if (!window.confirm("Eliminare questa email?\n⚠️ Verrà spostata nel cestino di Gmail (e rimossa dal CRM se importata).")) return;
                    try {
                      const p = account ? `?account=${encodeURIComponent(account)}` : "";
                      await api.delete(`/emails/gmail-archive/${sel.gmail_id}${p}`);
                      setRows((rs) => rs.filter((r) => r.gmail_id !== sel.gmail_id));
                      setSel(null);
                    } catch (e) { alert(e.response?.data?.detail || e.message); }
                  }} data-testid="wm-archive-delete">🗑 Elimina (anche da Gmail)</button>
                  <a className="btn sm" href={`https://mail.google.com/mail/u/0/#all/${sel.gmail_id}`} target="_blank" rel="noopener noreferrer" data-testid="wm-open-gmail">Apri in Gmail ↗</a>
                </>
              ) : (
                <Link className="btn sm" to={`/email/${sel.id}`} data-testid="wm-open-full">Scheda completa ↗</Link>
              )}
            </div>
            <div className="wm-headers" data-testid="wm-headers">
              <h3 style={{ margin: "0 0 8px" }}>{hl(sel.subject || "(senza oggetto)", q)}</h3>
              <div><strong>Da:</strong> {sel.from_name ? `${sel.from_name} ` : ""}&lt;{sel.from_email}&gt;</div>
              {sel.to?.length > 0 && <div><strong>A:</strong> {sel.to.join(", ")}</div>}
              {sel.cc?.length > 0 && <div data-testid="wm-cc"><strong>Cc:</strong> {sel.cc.join(", ")}</div>}
              <div><strong>Data:</strong> {sel.received_at ? new Date(sel.received_at).toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" }) : "—"}</div>
              {sel.account && <div><strong>Casella:</strong> {sel.account}</div>}
              <div><strong>Stato:</strong> {sel.archive ? "🗄 archivio Gmail" : <>{sel.status === "gestita" ? "✓ gestita" : "da gestire"}{sel.read ? " · letta" : ""}</>}</div>
              {sel.attachments?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <strong>Allegati:</strong>{" "}
                  {sel.archive ? (
                    <span className="muted">{sel.attachments.map((a) => a.filename).filter(Boolean).join(", ") || `${sel.attachments.length} allegati`} — usa "Apri in Gmail ↗" per scaricarli</span>
                  ) : sel.attachments.map((a, i) => (
                    <button key={i} className="btn sm" style={{ margin: "2px 4px 2px 0" }} onClick={() => openAttachment(i, a.filename)} data-testid={`wm-att-${i}`}>
                      📎 {a.filename || `allegato ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="wm-body">
              {sel.body_html ? (
                <iframe title="corpo" srcDoc={hlHtml(sel.body_html, q)} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" onLoad={fixFrameLinks} style={{ width: "100%", height: "100%", border: 0, background: "#fff", borderRadius: 8 }} />
              ) : (
                <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14 }}>{hl(sel.body_text, q)}</pre>
              )}
            </div>
          </>
        )}
      </section>

      {modal === "new" && (
        <EmailComposer mode="new" onClose={() => setModal(null)} onSent={() => { setModal(null); if (folder === "sent") load(); }} />
      )}
      {modal && modal !== "new" && (sel || selSent) && (
        <EmailComposer mode={modal} original={sel || sentAsOriginal(selSent)} onClose={() => setModal(null)} onSent={() => { setModal(null); load(); }} />
      )}
      {waDraft && (
        <WaDraftModal draft={waDraft} onClose={() => { setWaDraft(null); load(); }} onSent={() => { setWaDraft(null); load(); }} />
      )}
      {resumeDraft && (
        <EmailComposer mode="new" draft={resumeDraft} onClose={() => { setResumeDraft(null); load(); }} onSent={() => { setResumeDraft(null); load(); }} />
      )}
      {assignOpen && (
        <Modal title={`Abbina ${selected.size} email a una cartella`} onClose={() => setAssignOpen(false)}>
          <label>Azienda</label>
          <select value={selCompany} onChange={(e) => onCompany(e.target.value)} data-testid="wm-assign-company">
            <option value="">— scegli azienda —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label style={{ marginTop: 8 }}>Progetto</label>
          <select value={selProject} onChange={(e) => onProject(e.target.value)} disabled={!selCompany} data-testid="wm-assign-project">
            <option value="">— scegli progetto —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label style={{ marginTop: 8 }}>Cartella</label>
          <select value={selFolder} onChange={(e) => setSelFolder(e.target.value)} disabled={!selProject} data-testid="wm-assign-folder">
            <option value="">— scegli cartella —</option>
            {folders.filter((f) => !f.closed).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <div className="toolbar" style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={confirmAssign} data-testid="wm-assign-confirm">Abbina</button>
            <button className="btn" onClick={() => setAssignOpen(false)}>Annulla</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
